import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import BaseDimensionDrilldownPopover from '../../components/reportsWidgets/BaseDimensionDrilldownPopover';
import withTranslate from '../widgets/withTranslate';
import { getUserData } from '../../selectors/user';
import {
    defaultReportQueryParams,
    getDrilldownDimensions,
    isDrilldownDimensionBackendFilterable,
    mapToCreativesFilters,
    reportTypes,
} from '../../utils/reports';
import { getImpersonationFromQuery, serializeReportQuery } from '../../utils/url';
import { trackMixpanelEvent, REPORTS_EVENT_PREFIX } from '../../utils/general';
import { CUSTOMER_TIERS } from '../../utils/consts';
import { IN_OPERATOR } from '../../utils/reportsConsts';
import { API_VERSIONS, METRIC_COHORT_SEPARATOR, TIME_BREAKDOWN_PSEUDO_DIMENSIONS } from '../../customDashboards/utils';

// Defaults the Reports page applies to every query (see fieldPresets.commonDefaults and the
// reportConfig overrides in selectors/reports.js). The drilldown URL must carry these or the
// destination report runs unfiltered on attribution type / append tables / druid mode and
// produces different metric numbers than the widget.
const REPORT_QUERY_DEFAULTS = {
    source_attribution_type: ['dynamic'],
    with_append_tables: ['with_append'],
    druid_aggregated_data_sources_mode: ['not_aggregated'],
    skan_modeled_cohort_periods: [],
};

export function buildWidgetReportQuery(widgetQuery, reportDates, displayUnenriched = false) {
    if (!widgetQuery) {
        return null;
    }

    const actualWidgetQuery = widgetQuery;
    // Convert widget filters format to reports format
    const reportFilters = (actualWidgetQuery.filters || []).map(filter => ({
        dimension: filter.field,
        operator: filter.operator,
        values: [...(filter.values || [])],
    }));

    // The widget stores its time granularity as a pseudo-dimension (`__day`/`__week`/`__month`) inside
    // `dimensions`, but Reports expresses it via `time_breakdown` and must not see the pseudo-dimension
    // as a real one. Mirror buildQueryObject: derive the breakdown (`__week` -> `week`, `all` when none)
    // and drop the pseudo-dimension from `dimensions`, else the report ignores it and defaults to day.
    const widgetDimensions = actualWidgetQuery.dimensions || [];
    const timeBreakdownDimensions = widgetDimensions.filter(dim =>
        TIME_BREAKDOWN_PSEUDO_DIMENSIONS.some(tb => tb.name === dim)
    );

    const regularMetrics = [];
    const cohortMetricsSeen = new Set();
    const cohortMetrics = [];
    const cohortPeriods = new Set();

    (actualWidgetQuery.metrics || []).forEach(metric => {
        if (metric.includes(METRIC_COHORT_SEPARATOR)) {
            const parts = metric.split(METRIC_COHORT_SEPARATOR);
            const period = parts[parts.length - 1];
            const metricName = parts.slice(0, -1).join(METRIC_COHORT_SEPARATOR);

            // The widget stores `<metric>__<period>` for each (metric, period) pair, so the same
            // base metric appears once per period. Reports drilldown URLs list each base metric
            // only once — match that to avoid duplicate columns and inflated cohort metric counts.
            if (!cohortMetricsSeen.has(metricName)) {
                cohortMetricsSeen.add(metricName);
                cohortMetrics.push(metricName);
            }
            cohortPeriods.add(period);
        } else {
            regularMetrics.push(metric);
        }
    });

    const deserializedReportQuery = {
        ...defaultReportQueryParams,
        ...REPORT_QUERY_DEFAULTS,
        // Override with widget-specific values
        dimensions: widgetDimensions.filter(dim => !timeBreakdownDimensions.includes(dim)),
        time_breakdown: timeBreakdownDimensions.length === 0 ? ['all'] : [timeBreakdownDimensions[0].slice(2)],
        metrics: regularMetrics,
        cohort_metrics: cohortMetrics,
        cohort_periods: Array.from(cohortPeriods),
        filters: reportFilters,
        display_unenriched: displayUnenriched,
        ...(reportDates && {
            start_date: reportDates.startDate || '',
            end_date: reportDates.endDate || '',
            ...(reportDates.startDate2 && { start_date_2: reportDates.startDate2 }),
            ...(reportDates.endDate2 && { end_date_2: reportDates.endDate2 }),
        }),
    };

    // Add widget-specific fields if they exist (some widgets might not have these)
    if (actualWidgetQuery.dataTypeFields && actualWidgetQuery.dataTypeFields.length > 0) {
        deserializedReportQuery.dataTypeFields = [...actualWidgetQuery.dataTypeFields];
    }
    if (actualWidgetQuery.creativeGroupBy && actualWidgetQuery.creativeGroupBy.length > 0) {
        deserializedReportQuery.creativeGroupBy = [...actualWidgetQuery.creativeGroupBy];
    }
    if (actualWidgetQuery.skanDateDimensionName) {
        deserializedReportQuery.skanDateDimensionName = actualWidgetQuery.skanDateDimensionName;
    }

    return deserializedReportQuery;
}

// The report-shaping keys from a widget data type's `fields` (WIDGET_DATA_TYPES[dataType].fields) that
// belong in a Reports URL. A data type's `fields` also carry report-IDENTITY flags (query_type, is_skan,
// ...) which the report derives from its route, so those must never reach the URL. This is an ALLOWLIST
// on purpose: a new internal flag added to any data type fails safe (simply not copied) instead of
// silently leaking. When a report genuinely needs a new shaping field, add it here.
const REPORT_SHAPING_FIELD_KEYS = [
    'source_attribution_type',
    'confidence_interval_flag',
    'skan_validated_dimensions',
    'skan_redownloads_dimensions',
    'skan_date_dimension_name',
    'cross_device_cohort_type',
    'cohort_periods',
    'display_alignment',
];

export function buildWidgetReportQueryForType(
    widgetQuery,
    reportType,
    dataTypeFields,
    reportDates,
    displayUnenriched = false
) {
    const query = buildWidgetReportQuery(widgetQuery, reportDates, displayUnenriched);
    if (!query) {
        return null;
    }

    // The base builder emits camelCase keys the Reports URL deserializer ignores; re-emit as the
    // snake_case keys queryMap recognizes.
    delete query.dataTypeFields;
    delete query.creativeGroupBy;
    delete query.skanDateDimensionName;
    if (widgetQuery.dataTypeFields && widgetQuery.dataTypeFields.length > 0) {
        query.data_type_fields = [...widgetQuery.dataTypeFields];
    }
    if (widgetQuery.skanDateDimensionName) {
        query.skan_date_dimension_name = widgetQuery.skanDateDimensionName;
    }

    // Copy ONLY the report-shaping fields from the widget's data-type config (allowlist), applied after
    // the standard defaults so per-type values win (e.g. source_attribution_type ['skan'] over the
    // default ['dynamic']). Identity flags (query_type, is_*) are never in the allowlist, so they can't
    // leak into the URL.
    const fields = dataTypeFields || {};
    REPORT_SHAPING_FIELD_KEYS.forEach(key => {
        if (key in fields) {
            query[key] = fields[key];
        }
    });

    // Unified report expresses cohorts via group fields, not standard cohort_metrics/cohort_periods.
    if (reportType === reportTypes.unifiedReport) {
        query.cohort_metric_group_fields = query.cohort_metrics || [];
        query.unified_cohort_periods = query.cohort_periods || [];
        query.cohort_metrics = [];
        query.cohort_periods = [];
    }

    // Creatives report needs creative grouping + api v2 (mirrors buildQueryObject's CREATIVES branch).
    if (reportType === reportTypes.creativeExplore) {
        query.creative_group_by = widgetQuery.creativeGroupBy || [];
        query.api_version = API_VERSIONS.VERSION_2;
    }

    return query;
}

export function buildDashboardDrilldownRedirectQuery(
    widgetQuery,
    nodeData,
    dimensionField,
    reportDates,
    availableFilters = [],
    displayUnenriched = false
) {
    const deserializedReportQuery = buildWidgetReportQuery(widgetQuery, reportDates, displayUnenriched);
    if (!deserializedReportQuery) {
        return '';
    }

    (widgetQuery.dimensions || []).forEach(dimension => {
        if (!nodeData[dimension]) {
            return;
        }
        const isFilterable = isDrilldownDimensionBackendFilterable(dimension, availableFilters);
        const existingFilter = deserializedReportQuery.filters.find(
            filter => filter.dimension === dimension && filter.operator === IN_OPERATOR
        );

        if (existingFilter) {
            if (!existingFilter.values.includes(nodeData[dimension])) {
                existingFilter.values.push(nodeData[dimension]);
            }
        } else if (isFilterable) {
            deserializedReportQuery.filters.push({
                dimension,
                operator: IN_OPERATOR,
                values: [nodeData[dimension]],
            });
        } else {
            deserializedReportQuery.post_process_filters_ui = deserializedReportQuery.post_process_filters_ui || {};
            deserializedReportQuery.post_process_filters_ui[dimension] = {
                filter: nodeData[dimension],
                type: 'contains',
            };
        }
    });

    // Add the new dimension being drilled down to
    deserializedReportQuery[dimensionField.type] = deserializedReportQuery[dimensionField.type] || [];

    if (dimensionField.path === '/creatives') {
        deserializedReportQuery.filters = mapToCreativesFilters(deserializedReportQuery.filters);
        deserializedReportQuery.creativeGroupBy = ['image'];
        deserializedReportQuery.display_alignment = true;
        deserializedReportQuery.api_version = 1;
        deserializedReportQuery.displayNonGraphical = true;
    } else if (dimensionField.values) {
        deserializedReportQuery[dimensionField.type].push(...dimensionField.values);
    } else {
        deserializedReportQuery[dimensionField.type] =
            dimensionField.type === 'time_breakdown'
                ? [dimensionField.name]
                : deserializedReportQuery[dimensionField.type].concat(dimensionField.name);
    }

    return serializeReportQuery(deserializedReportQuery, null, null, null, null);
}

function DashboardDimensionDrilldownPopover({
    widgetMetadata = null,
    widgetQuery = null,
    reportDates = null,
    availableFilters = [],
    displayUnenriched = false,
    nodeData = null,
    anchorEl = null,
    showPopover = true,
    closePopover = () => {},
    translate = () => {},
}) {
    const userData = useSelector(getUserData);

    // Use widget metadata instead of reports state
    const metadata = widgetMetadata || {};
    const isStandardAnalytics = userData.organization_tier === CUSTOMER_TIERS.STANDARD_ANALYTICS;

    // Generate dimension list using the same logic as the reports selector
    // Filter by dashboardVisible flag and exclude dimensions already in the widget
    const availableDrilldownDimensions = getDrilldownDimensions(metadata).filter(
        ({ name, values = [], dashboardVisible = true }) => {
            if (!dashboardVisible) return false;
            return !(metadata[name] || values.find(value => metadata[value]));
        }
    );

    const dimensionList = isStandardAnalytics
        ? availableDrilldownDimensions.filter(dimension => dimension.standardAnalyticsVisible)
        : availableDrilldownDimensions;

    const handleDimensionSelection = (dimensionField, nodeData) => {
        if (!widgetQuery) {
            return;
        }

        // Use our custom dashboard redirect query builder
        const redirectQuery = buildDashboardDrilldownRedirectQuery(
            widgetQuery,
            nodeData,
            dimensionField,
            reportDates,
            availableFilters,
            displayUnenriched
        );

        // Build URL following the same pattern as other utilities
        const redirectPageName = dimensionField.path ? dimensionField.path.substring(1) : 'react/reports'; // Remove leading slash if present
        const { href, hash } = window.location;
        const redirectHref = href.substring(0, href.indexOf(hash) + 1);

        // Check for impersonation parameter
        const impersonationUser = getImpersonationFromQuery();
        let redirectUrl = `${redirectHref}/${redirectPageName}?${redirectQuery}`;

        if (impersonationUser) {
            redirectUrl += `&user=${encodeURIComponent(impersonationUser)}`;
        }

        trackMixpanelEvent(REPORTS_EVENT_PREFIX, 'Drilldown dimension clicked', {
            widget_query: widgetQuery,
            drilldown_dimension: dimensionField.display_name,
        });

        window.open(redirectUrl, '_blank');
    };

    return (
        <BaseDimensionDrilldownPopover
            dimensionList={dimensionList}
            nodeData={nodeData}
            anchorEl={anchorEl}
            showPopover={showPopover}
            closePopover={closePopover}
            onDimensionSelection={handleDimensionSelection}
            translate={translate}
        />
    );
}

DashboardDimensionDrilldownPopover.propTypes = {
    widgetMetadata: PropTypes.any,
    widgetQuery: PropTypes.any,
    reportDates: PropTypes.any,
    availableFilters: PropTypes.arrayOf(PropTypes.any),
    displayUnenriched: PropTypes.bool,
    nodeData: PropTypes.any,
    showPopover: PropTypes.bool,
    anchorEl: PropTypes.instanceOf(Element),
    closePopover: PropTypes.func,
    translate: PropTypes.func,
};

export default withTranslate(DashboardDimensionDrilldownPopover);
